import React from 'react'
import PropTypes from 'prop-types'
import MuiTextField from '@mui/material/TextField'

const SIZE_PROPS = {
  sm: { size: 'small' },
  default: { size: 'medium' },
}

// Closed-state box only — pixel-matched to Components/ui/Select's own
// CLOSED_SX (height 40, border var(--vo-line), focus var(--color-brand), 'Lato' 13px) so a
// migrated TextField sits flush with an adjacent migrated Select, zero
// visible seam, on every shared property (height/border color+width/font).
// Border-radius needs no entry here: muiTheme's shape.borderRadius (10)
// already applies to both components' fieldsets identically.
// Skipped for size="sm" (a genuinely smaller variant, unused today but kept
// distinct on purpose) and for multiline (grows with `rows`; a fixed-height
// closed box has no meaning there, mirroring Select skipping its own
// CLOSED_SX in `multiple` mode).
const CLOSED_SX = {
  // .MuiOutlinedInput-input doubled: same specificity-tie reasoning as
  // Select's CLOSED_SX — MUI's own generated size-variant class otherwise
  // wins over a single-class sx selector.
  '& .MuiOutlinedInput-input.MuiOutlinedInput-input': {
    boxSizing: 'border-box',
    height: '40px',
    padding: '0 12px',
    fontFamily: "'Lato', sans-serif",
    fontSize: '13px',
    color: 'var(--vo-ink)',
  },
  // TextField's `sx` targets the outer FormControl, not the OutlinedInput
  // root itself (unlike Select, whose `sx` lands directly on MuiSelect) —
  // so hover/focus need a descendant selector on `.MuiOutlinedInput-root`
  // rather than Select's `&:hover` / `&.Mui-focused` compound form.
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--vo-line)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--vo-line)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-brand)',
    borderWidth: '1px',
  },
}

export const TextField = React.forwardRef(function TextField({
  label, error, helperText, fullWidth, size, multiline, rows,
  disabled, sx, ...rest
}, ref) {
  return (
    <MuiTextField
      ref={ref}
      label={label}
      error={!!error}
      helperText={helperText}
      fullWidth={fullWidth}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      disabled={disabled}
      variant="outlined"
      {...SIZE_PROPS[size]}
      sx={multiline || size === 'sm' ? [{}, sx] : [CLOSED_SX, sx]}
      {...rest}
    />
  )
})

TextField.propTypes = {
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'default']),
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  disabled: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

TextField.defaultProps = { size: 'default', error: false, sx: {} }
